/**
 * Story Book Corner's data: the shelves of books, and each book's activities.
 * Pure functions, so tested (storyCorner.test.js).
 *
 * The Corner is the one way into the stories (2026-10-28: the old Story path,
 * choose-path -> story units -> story games, folded into it). A book is the
 * pages of one `story` in storybookSentence; its activities are the story
 * games in gameData whose `category` is that story, one row per part (`target`
 * 11, 12...). Books with no game rows are read-only.
 */

// the Story Book game is the reader itself now, not an activity of its own
const NOT_ACTIVITIES = new Set(["Story Book"]);

// Corner games are all playable whatever level the child picked on the home
// screen (Spencer's call): 5 is the level at which every game shows
export const CORNER_DIFFICULTY = 5;

/**
 * A query string with spaces as %20 (URLSearchParams writes "+", which the
 * app's own param reader, getSearchParams, doesn't turn back into a space:
 * "Story 1" became "Story+1").
 *
 * @param {Object<string, string>} values
 */
export const queryString = (values) =>
  Object.entries(values)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");

const hasGame = (row, gameType) => {
  const value = row?.[gameType];
  return value !== undefined && value !== null && value !== "" && String(value) !== "null";
};

/**
 * The shelves: one per category, its books in the sheet's order, each with its
 * cover (the first page's picture) and title (the first page's sentence).
 *
 * @param {Array} sentences - storybookSentence
 * @returns {Array<{category: string, books: Array<{story: string, title: string, cover: string, pages: number}>}>}
 */
export const storyShelves = (sentences = []) => {
  const shelves = new Map();
  sentences.forEach((row) => {
    const category = String(row?.category ?? "").trim();
    const story = String(row?.story ?? "").trim();
    if (!category || !story) return;
    if (!shelves.has(category)) shelves.set(category, new Map());
    const books = shelves.get(category);
    if (!books.has(story)) books.set(story, []);
    books.get(story).push(row);
  });
  return [...shelves].map(([category, books]) => ({
    category,
    books: [...books].map(([story, pages]) => {
      const first = [...pages].sort((a, b) => Number(a.page) - Number(b.page))[0];
      const title = String(first?.fullSentence ?? first?.English ?? "").trim();
      return {
        story,
        title: title || story,
        cover: first?.App_Art ?? "",
        pages: pages.filter((p) => Number(p.page) > 0).length,
      };
    }),
  }));
};

/**
 * A book's activities, part by part: the story games for that story, each
 * with the address that starts it in the game frame (the same address the
 * old story menus used, so the games need no changes).
 *
 * @param {Object} args
 * @param {string} args.story - The book's `story`
 * @param {Array} args.gameData
 * @param {Array} args.storybookVocab - Gives the unit number (`lesson`)
 * @param {string[]} args.possibleGames - config.PossibleGames, in display order
 * @returns {Array<{target: string, label: string, games: Array<{gameType: string, key: string, search: string}>}>}
 */
export const storyActivities = ({ story, gameData = [], storybookVocab = [], possibleGames = [] }) => {
  const lesson = storybookVocab.find((v) => String(v?.homeName ?? "").trim() === story)?.lesson;
  if (lesson === undefined || lesson === null || lesson === "") return [];
  const unit = `${lesson}${story}`;
  return gameData
    .filter((row) => String(row?.category ?? "").trim() === story && row?.target !== undefined)
    .sort((a, b) => Number(a.target) - Number(b.target))
    .map((row, i) => ({
      target: String(row.target),
      label: `Part ${i + 1}`,
      games: possibleGames
        .filter((gameType) => !NOT_ACTIVITIES.has(gameType) && hasGame(row, gameType))
        .map((gameType) => ({
          gameType,
          key: `${row.Key}${gameType}`,
          search: queryString({
            difficulty: CORNER_DIFFICULTY,
            unit,
            "game-name": row.target,
            "game-type": gameType,
            Key: `${row.Key}${gameType}`,
          }),
        })),
    }))
    .filter((part) => part.games.length > 0);
};

/**
 * Which book an old story address was about: its `game-name` (a part's
 * target) or its `unit` ("1Story 1"). For the redirects from the retired
 * story menus.
 *
 * @param {Object} args
 * @param {string} [args.target] - `game-name`
 * @param {string} [args.unit] - `unit`
 * @param {Array} args.gameData
 * @returns {string|null} the story, or null
 */
export const storyFor = ({ target, unit, gameData = [] }) => {
  if (target !== undefined && target !== null && target !== "") {
    const row = gameData.find((d) => String(d?.target) === String(target));
    if (row?.category) return String(row.category).trim();
  }
  if (unit) {
    const story = String(unit).replace(/^\d+/, "").trim();
    if (story) return story;
  }
  return null;
};

/**
 * Where "back" goes from a story place, or null for anywhere else (goBack in
 * nonUIFuncs handles those):
 *
 * - a story game (its unit is a story, "1Story 1") -> that book's page
 * - reading a book -> its page
 * - a book's page -> the shelves
 * - the shelves -> home
 *
 * @param {string} pathname
 * @param {string} search
 * @returns {{pathname: string, search: string}|null}
 */
export const cornerBack = (pathname, search = "") => {
  const params = new URLSearchParams(search);
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/start-game") {
    const unit = params.get("unit") ?? "";
    if (!unit.includes("Story")) return null;
    const story = unit.replace(/^\d+/, "").trim();
    return { pathname: "/story-book-corner", search: `?${queryString({ story })}` };
  }
  if (path === "/story-book-corner") {
    const story = params.get("story");
    if (story && params.get("read") === "1") {
      return { pathname: "/story-book-corner", search: `?${queryString({ story })}` };
    }
    if (story) return { pathname: "/story-book-corner", search: "" };
    return { pathname: "/", search: "" };
  }
  return null;
};

/**
 * A page's vocabulary words (storybookVocab rows for this story and page),
 * the words the old Story Book game listed under each page.
 *
 * @returns {Array<{key: string, word: string, english: string, audio: string}>}
 */
export const pageVocab = ({ story, page, storybookVocab = [] }) =>
  storybookVocab
    .filter(
      (v) =>
        String(v?.homeName ?? "").trim() === story &&
        String(v?.page ?? "") === String(page) &&
        String(v?.Language ?? "").trim(),
    )
    .map((v, i) => ({
      key: `${v.Key ?? i}`,
      word: String(v.Language).trim(),
      english: String(v.English ?? "").trim(),
      audio: String(v.App_Audio ?? "").trim(),
    }));

// Reading a book earns what the old Story Book game gave for one read: 9
// stars for each of its parts, at the Story Book game's own first level.
// The level system counts 9 per part (buildScoreData), so re-reading never
// adds more.
const READING_STARS = 9;
const READING_DIFFICULTY = "easy";

/**
 * What finishing a book saves: one score per part that has a Story Book game,
 * shaped like the game frame's (FullGameContainer's findStars for stories),
 * and how many stars that adds to the player's total (only what's new).
 *
 * @param {Object} args
 * @param {string} args.story
 * @param {Array} args.gameData
 * @param {Array} args.scoreData - The player's scores (App's scoreData)
 * @param {number} [args.time] - Seconds spent reading
 * @returns {{scores: Array<Object>, starsToAdd: number}}
 */
export const readingScores = ({ story, gameData = [], scoreData = [], time = 0 }) => {
  const scores = [];
  let starsToAdd = 0;
  gameData
    .filter((row) => String(row?.category ?? "").trim() === story && hasGame(row, "Story Book"))
    .forEach((row) => {
      const saved = scoreData.find((d) => d.Key === `${row.Key}Story Book`);
      if (!saved) return; // not a game this player's scores know about
      const before = saved.difficultyScores?.[READING_DIFFICULTY]?.stars ?? 0;
      starsToAdd += Math.max(0, READING_STARS - before);
      scores.push({
        Key: row.Key,
        gameType: "Story Book",
        difficulty: READING_DIFFICULTY,
        score: 0,
        time,
        correct: 0,
        incorrect: 0,
        stars: READING_STARS,
        firstStars: READING_STARS,
        firstNextGoals: null,
        secondStars: null,
        secondNextGoals: null,
      });
    });
  return { scores, starsToAdd };
};

/** The shelf (category) a book is on, or null. */
export const shelfOf = (sentences = [], story) =>
  sentences.find((row) => String(row?.story ?? "").trim() === story)?.category ?? null;
